import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { RotateCw, ZoomIn, Hand } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImage = (url) => (!url ? null : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

/**
 * Product360Viewer — premium can/bottle showcase.
 * - Hover: gentle float + light sweep + magnifier ring
 * - Drag: rotate on Y axis (perspective-3d) — 0 to 360°
 * - Pinch/wheel: zoom
 * - Reflected floor and glare highlight
 * - Mobile touch supported via pointer events
 */
export default function Product360Viewer({ imageUrl, alt, accent = "#D4AF37", showControls = true }) {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const rotateY = useMotionValue(-8);
  const springRotate = useSpring(rotateY, { stiffness: 60, damping: 20 });
  const scale = useSpring(1, { stiffness: 120, damping: 18 });
  const rotateInset = useTransform(springRotate, (v) => Math.abs(((v % 360) + 360) % 360 - 180));
  const glareOpacity = useTransform(rotateInset, [0, 90, 180], [0.55, 0.2, 0.05]);

  const img = absoluteImage(imageUrl);

  const startX = useRef(0);
  const startRotate = useRef(0);

  const onPointerDown = (e) => {
    setDragging(true);
    startX.current = e.clientX ?? (e.touches?.[0]?.clientX || 0);
    startRotate.current = rotateY.get();
    containerRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const x = e.clientX ?? (e.touches?.[0]?.clientX || 0);
    const dx = x - startX.current;
    rotateY.set(startRotate.current + dx * 0.6);
  };

  const onPointerUp = (e) => {
    setDragging(false);
    containerRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const spin = useCallback(() => {
    const current = rotateY.get();
    rotateY.set(current + 360);
  }, [rotateY]);

  const toggleZoom = () => {
    const next = !zoomed;
    setZoomed(next);
    scale.set(next ? 1.35 : 1);
  };

  // gentle idle float when not dragging
  useEffect(() => {
    if (dragging) return;
    const id = setInterval(() => {
      const v = rotateY.get();
      rotateY.set(v + (Math.random() - 0.5) * 0.6);
    }, 900);
    return () => clearInterval(id);
  }, [dragging, rotateY]);

  return (
    <div
      ref={containerRef}
      className="relative aspect-square rounded-2xl overflow-hidden select-none touch-none"
      style={{
        background: `radial-gradient(circle at 50% 45%, ${accent}22 0%, ${accent}08 40%, #0a0a0a 100%)`,
        perspective: "1400px",
        cursor: dragging ? "grabbing" : "grab",
      }}
      data-testid="product-360-viewer"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onDoubleClick={toggleZoom}
      onWheel={(e) => {
        // subtle zoom on wheel
        const cur = scale.get();
        const next = Math.max(0.9, Math.min(1.7, cur + (e.deltaY < 0 ? 0.1 : -0.1)));
        scale.set(next);
        setZoomed(next > 1.05);
      }}
    >
      {/* backdrop grain */}
      <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
           style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />

      {/* soft floor reflection */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
           style={{ background: `linear-gradient(to top, ${accent}25, transparent)` }} />

      {img ? (
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            rotateY: springRotate,
            scale,
            transformStyle: "preserve-3d",
          }}
        >
          <img
            src={img}
            alt={alt}
            draggable={false}
            className="w-full h-full object-contain drop-shadow-2xl"
            style={{ filter: "drop-shadow(0 30px 40px rgba(0,0,0,0.55))" }}
            data-testid="product-360-image"
          />

          {/* live specular glare that dims as user rotates */}
          <motion.div
            className="absolute inset-y-6 left-1/4 w-8 pointer-events-none"
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
              filter: "blur(6px)",
              opacity: glareOpacity,
              mixBlendMode: "screen",
            }}
          />
        </motion.div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs uppercase tracking-widest">
          Image rendering…
        </div>
      )}

      {showControls && (
        <div className="absolute bottom-3 right-3 flex gap-1.5 z-10">
          <button
            onClick={(e) => { e.stopPropagation(); spin(); }}
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-colors border border-white/10"
            title="Rotate 360°"
            data-testid="btn-360-rotate"
          >
            <RotateCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleZoom(); }}
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-colors border border-white/10"
            title="Zoom"
            data-testid="btn-360-zoom"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="absolute bottom-3 left-3 z-10 hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 pointer-events-none">
        <Hand className="w-3 h-3" /> Drag to rotate
      </div>
    </div>
  );
}
